import { axiosRubyClient } from '@/util/axios/axiosClient';

// Read-only Actions Log feed over shift_events (internal/tenant-admin only).
// Backed by GET /api/admin/shift_events (+ /filters). See GRAVY-10196.

export type ShiftEventActorType = 'ops' | 'worker' | 'system';

export interface ShiftEventActor {
  type: ShiftEventActorType;
  id?: number | null;
  name?: string | null;
}

export interface ShiftEventShift {
  id: number;
  start_at: string | null;
  end_at: string | null;
  job_id: number | null;
  order_id: number | null;
  client_name: string | null;
  // Job-derived details for the Operations log rows (any may be null).
  position: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  location: string | null; // "DC 20002" (state + ZIP)
  pay_rate: string | null; // "$32.45/hr"
  pay_rate_cents: number | null;
  slots_total: number | null;
  slots_filled: number | null;
}

// A display-ready old -> new value change (e.g. a pay-rate edit).
export interface ShiftEventChange {
  field: string; // "Pay rate"
  from: string | null; // "$32.45/hr"
  to: string | null; // "$35.00/hr"
}

// A worker referenced by an event row (hired at cancellation, invited, etc.).
export interface ShiftEventWorker {
  id: number;
  name: string | null;
}

// Backwards-compatible alias for the worker shape hired at cancellation time.
export type ShiftEventHiredWorker = ShiftEventWorker;

// Invitation context captured for a `shift.invitation.sent` event, so the
// Actions Log can show who was invited and how instead of a bare description.
export interface ShiftEventInvitation {
  invited_worker_count: number;
  invited_workers: ShiftEventWorker[];
  sent_by: number | null; // sender user id (also surfaced as the row actor)
  channel: string | null; // "sms" | "push" | ...
}

// Cancellation context captured for a `shift.cancelled` event, so the Actions
// Log reflects the state at cancel time rather than the current job state.
export interface ShiftEventCancellation {
  reason: string | null; // "Client cancelled"
  hours_before_shift: string | null; // "3h 30m before shift"
  hired_worker_count: number; // hired workers captured at cancellation time
  hired_workers: ShiftEventHiredWorker[];
}

// Expiration context captured for a `shift.invitation.expired` event, so the
// Actions Log can show whose invitation lapsed and the offer's expiry window.
// System-triggered, so the worker is surfaced here rather than as the row actor.
export interface ShiftEventExpiration {
  worker: ShiftEventWorker | null;
  expiry_window_minutes: number | null; // offer lifetime, e.g. 120 -> "2h"
}

// Hire context captured for a `shift.worker.hired` event, so the Actions Log can
// show who was hired and how (admin approval vs. worker self-service) instead of
// a bare "Worker hired" line. approved_by is null for self-service claim/offer
// paths (no admin approver); for admin hires it is also surfaced as the row actor.
export interface ShiftEventHire {
  worker: ShiftEventWorker | null;
  approval_type: string | null; // "claim" | "offer_accepted" | "manual" | "order"
  approved_by: ShiftEventWorker | null; // approving admin, null for self-service
}

// Reason context for the three vacancy events — shift.worker.dropped
// (drop_reason), shift.invitation.declined (decline_reason), and
// worker.removed_from_shift (removal_reason) — so the Actions Log can show who
// left and why instead of a bare "Worker dropped the shift" line. The worker is
// surfaced here because ops/system-triggered rows don't carry it as the actor.
export interface ShiftEventReason {
  worker: ShiftEventWorker | null;
  reason: string | null; // "sick" | "double-booked" | ...
  // Decline source, populated only for shift.invitation.declined
  // ("decline_mobile" | "reject_v1" | "reject_v2"); null for drop/removal and
  // for declines that predate its capture. Workers give no reason when
  // declining, so this is the auditable "how" for that event.
  declined_via: string | null;
}

// A causal reference to the drop/removal event a backfill re-invite followed,
// resolved server-side from the captured drop_event_id, so the Actions Log can
// render a visible link like "Following drop by <worker>". Null when nothing
// was linked or the referenced event no longer exists.
export interface ShiftEventDropReference {
  event_id: string;
  event_type: string; // "shift.worker.dropped" | "worker.removed_from_shift"
  worker: ShiftEventWorker | null;
}

// Backfill context captured for a `shift.backfill.invite_sent` event, so the
// Actions Log can show who was re-invited, which pool the re-invite drew from,
// and the drop/removal it followed instead of a bare description. The event is
// system-triggered (no human sender), so recipients surface here, not as the
// row actor.
export interface ShiftEventBackfill {
  recipient_worker_count: number;
  recipient_workers: ShiftEventWorker[];
  pool_source: string | null; // "original" | "expanded"
  following: ShiftEventDropReference | null;
}

// Slot-fill context captured for a `shift.backfill.slot_filled` event, so the
// Actions Log can show who filled the vacated slot, who departed, how long the
// slot stood open, and the drop/removal it followed instead of a bare "Backfill
// slot filled" line. The event is system-triggered and its payload names the two
// workers separately, so both surface here rather than on the row actor.
export interface ShiftEventBackfillFill {
  replacement_worker: ShiftEventWorker | null;
  original_worker: ShiftEventWorker | null;
  minutes_to_fill: number | null; // how long the slot stood open, e.g. 45 -> "45 min"
  following: ShiftEventDropReference | null;
}

// Check-in context captured for a `shift.worker.checked_in` event, so the
// Actions Log can show how the worker checked in, whether their location was
// GPS-verified, and how early/late they were relative to the scheduled start —
// instead of a bare "Worker checked in" line. Shared by all five check-in flows
// (worker app/QR/manual and the two ops overrides). The worker is surfaced here
// because ops-initiated flows put the admin (not the worker) on the actor line.
export interface ShiftEventCheckin {
  worker: ShiftEventWorker | null;
  check_in_method: string | null; // "app" | "manual_app" | "qr" | "ops_manual" | "ops_enter_hours"
  location_verified: boolean | null; // GPS check (false for ops overrides, null if unknown)
  minutes_relative_to_start: number | null; // <0 early, >0 late, relative to shift start
}

// Check-out context captured for a `shift.worker.checked_out` event, so the
// Actions Log can show how the worker checked out, the actual hours worked, and
// whether they left before the scheduled end — instead of a bare "Worker checked
// out" line. Shared by all check-out flows (worker app/QR/manual and the two ops
// overrides). The worker is surfaced here because ops-initiated flows put the
// admin (not the worker) on the actor line. Any field may be null on legacy
// events that predate its capture.
export interface ShiftEventCheckout {
  worker: ShiftEventWorker | null;
  check_out_method: string | null; // "app" | "manual_app" | "qr" | "ops_manual" | "ops_enter_hours"
  actual_hours_worked: number | null; // hours worked, from check-in to check-out
  early_departure: boolean | null; // left before the scheduled end (null if unknown)
}

// Start context captured for a `shift.started` event, so the Actions Log can
// show who was expected on the shift and the fill snapshot at start time —
// instead of only the generic shift row (GRAVY-10196). The event is system-
// emitted by the lifecycle sweep (no human actor), so the expected workers are
// surfaced here rather than on the actor line.
export interface ShiftEventStarted {
  expected_worker_count: number;
  expected_workers: ShiftEventWorker[];
  slots_filled_count: number; // fill snapshot captured at shift start
}

// Completion context captured for a `shift.completed` event, so the Actions Log
// can show attendance (workers present, no-shows) and the final pay rate at
// completion — instead of only the generic shift row (GRAVY-10196). The event is
// system-emitted by the lifecycle sweep (no human actor). Any field may be null
// on legacy events that predate its capture.
export interface ShiftEventCompleted {
  workers_present_count: number | null;
  no_show_count: number | null;
  final_pay_rate: string | null; // "$32.45/hr"
  final_pay_rate_cents: number | null;
}

// Reminder context captured for a `comms.reminder.sent` event, so the Actions
// Log can show who the reminder went to, the channel, the reminder type, and how
// long before the shift it fired — instead of only the generic shift row
// (GRAVY-10196). The event is automated/system-emitted (no human actor), so the
// worker is surfaced here rather than on the actor line. Any field may be null.
export interface ShiftEventReminder {
  worker: ShiftEventWorker | null;
  channel: string | null; // "push" | "sms"
  reminder_type: string | null; // "checkin" | "confirmation" | "reconfirmation"
  hours_before_shift: string | null; // "45 min before shift"
}

// Late-flag context captured for a `shift.worker.late_flagged` event, so the
// Actions Log can show who was flagged, how many minutes after the scheduled
// start the flag fired, and whether ops was alerted — instead of a bare "Worker
// flagged as late" row that can't be told apart when a shift has several. The
// event is system-emitted by the late sweep (no human actor), so the worker is
// surfaced here rather than on the actor line. Any field may be null on legacy
// events that predate its capture.
export interface ShiftEventLate {
  worker: ShiftEventWorker | null;
  minutes_after_start: number | null; // minutes past the scheduled start when flagged
  ops_alerted: boolean | null; // whether ops was alerted (null if unknown)
}

// No-show context captured for a `shift.worker.no_show` event, so the Actions Log
// can show who was flagged as a no-show, who/what flagged it (an ops admin or the
// automated sweep), and how many minutes after the scheduled start the flag fired
// — instead of a bare "Worker flagged as no-show" row that can't be told apart
// across workers. The affected worker is never the row actor (a manual flag
// surfaces the flagging admin; an automated flag is system-triggered), so the
// worker is surfaced here. flagged_by uses the same actor vocabulary as the row
// actor: { type: 'ops', id, name } or { type: 'system' }.
export interface ShiftEventNoShow {
  worker: ShiftEventWorker | null;
  flagged_by: ShiftEventActor | null; // ops admin who flagged it, or { type: 'system' }
  minutes_after_shift_start: number | null; // minutes past the scheduled start when flagged
}

// Pool-expansion context captured for a `shift.pool.expanded` event, so the
// Actions Log can show how far the notification tier moved (previous -> new), the
// size of the newly-activated worker pool, and how long before the shift it fired
// — instead of only the generic shift row (GRAVY-10196). trigger_source and
// triggered_by are forward-compatible: they stay null until the trigger/actor
// capture (Part B) ships, and surface automatically once the payload carries them.
export interface ShiftEventPoolExpanded {
  previous_tier: number | null;
  new_tier: number | null;
  new_tier_pool_size: number | null; // worker count of the newly-activated tier
  trigger_source: string | null; // why it expanded — null until Part B captures it
  triggered_by: ShiftEventActor | null; // manual tier-send actor — null until Part B
  hours_before_shift_start: string | null; // "6h 30m before shift"
}

// Accept context captured for a `shift.invitation.accepted` event, so the Actions
// Log can show HOW the worker accepted — a direct invitation accept vs. claiming
// an open offer — instead of a bare "Worker accepted invitation" line. The event
// is worker-initiated, so the worker already surfaces on the row actor; it is
// also resolved here for a self-contained block. claimed_via_label is a
// user-friendly form computed server-side ("Accepted invitation" / "Claimed from
// offer"); any field may be null on legacy events that predate its capture.
export interface ShiftEventInvitationAccepted {
  worker: ShiftEventWorker | null;
  claimed_via: string | null; // "offer_accept" | "offer_claim"
  claimed_via_label: string | null; // "Accepted invitation" | "Claimed from offer"
}

// Rating context captured for an `ops.worker.rated` event, so the Actions Log can
// show WHICH worker was rated, WHO rated them (an ops/client admin), the rating
// value, the reason/comment, and any tags — instead of a bare "Worker rated" line
// (GRAVY-10196). The rating admin also surfaces on the row actor and is resolved
// here for a self-contained block. Optional fields are null/empty when not
// provided, so callers must omit them rather than render a blank label.
export interface ShiftEventWorkerRating {
  worker: ShiftEventWorker | null;
  rated_by: ShiftEventWorker | null; // rating admin (id + name), null if unrecorded
  rating: number | null;
  rating_reason: string | null; // free-text comment, when provided
  tags: string[]; // rating tags, empty when none
}

// Pay-rate context captured for a `shift.pay_rate.changed` event beyond the old
// -> new rate already carried in `changes`: the pricing rule_id that drove an
// automated change (GRAVY-10196). rule_id is reserved for automated pricing/rule
// scenarios and is null for a manual change, so the UI omits the label when null.
export interface ShiftEventPayRate {
  rule_id: string | null; // pricing rule that drove the change; null for manual
}

export interface ShiftEventEntry {
  id: number;
  event_id: string;
  event_type: string;
  triggered_by: string;
  created_at: string | null;
  description: string;
  actor: ShiftEventActor;
  shift: ShiftEventShift | null;
  changes: ShiftEventChange[];
  cancellation: ShiftEventCancellation | null;
  invitation: ShiftEventInvitation | null;
  expiration: ShiftEventExpiration | null;
  hire: ShiftEventHire | null;
  reason: ShiftEventReason | null;
  backfill: ShiftEventBackfill | null;
  backfill_fill: ShiftEventBackfillFill | null;
  checkin: ShiftEventCheckin | null;
  checkout: ShiftEventCheckout | null;
  started: ShiftEventStarted | null;
  completed: ShiftEventCompleted | null;
  reminder: ShiftEventReminder | null;
  pay_rate: ShiftEventPayRate | null;
  late: ShiftEventLate | null;
  no_show: ShiftEventNoShow | null;
  pool_expanded: ShiftEventPoolExpanded | null;
  invitation_accepted: ShiftEventInvitationAccepted | null;
  worker_rating: ShiftEventWorkerRating | null;
  json_data: Record<string, unknown> | null;
}

export interface Pagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ShiftEventsResponse {
  success: boolean;
  data: ShiftEventEntry[];
  pagination: Pagination;
}

export interface ShiftEventFilterOption {
  id: number;
  name: string;
}

export interface ShiftEventFiltersResponse {
  success: boolean;
  data: {
    users: ShiftEventFilterOption[];
    clients: ShiftEventFilterOption[];
  };
}

export interface ShiftEventsParams {
  page?: number;
  per_page?: number;
  search?: string;
  user_id?: number | string;
  client_id?: number | string;
  date?: string;
}

export const fetchShiftEvents = async (
  params: ShiftEventsParams,
): Promise<ShiftEventsResponse> => {
  const response = await axiosRubyClient.get('/api/admin/shift_events', {
    params,
  });
  return response.data;
};

export const fetchShiftEventFilters =
  async (): Promise<ShiftEventFiltersResponse> => {
    const response = await axiosRubyClient.get(
      '/api/admin/shift_events/filters',
    );
    return response.data;
  };
